import { useCallback, useEffect, useRef } from 'react';

// Define the available event types and their respective argument types
export type EventMap = {
  beforeSubmit: null;
};

class EventEmitter<T extends Record<string, unknown>> {
  private events: { [K in keyof T]?: Set<(args: T[K]) => Promise<void>> } = {};

  // Subscribe to a specific event type
  subscribe<K extends keyof T>(
    event: K,
    handler: (args: T[K]) => Promise<void>
  ): () => void {
    if (!this.events[event]) {
      this.events[event] = new Set();
    }
    this.events[event]!.add(handler);

    // Return an unsubscribe function
    return () => {
      this.events[event]!.delete(handler);
    };
  }

  async trigger<K extends keyof T>(event: K, args: T[K]): Promise<void> {
    if (this.events[event]) {
      const handlers = Array.from(this.events[event]!);
      await Promise.all(handlers.map((handler) => handler(args)));
    }
  }
}

const eventEmitter = new EventEmitter<EventMap>();

export function useEventEmitterSubscribe<K extends keyof EventMap>(
  event: K,
  handler: (args: EventMap[K]) => Promise<void>
) {
  const handlerRef = useRef(handler);

  // Always keep the ref updated to the latest handler
  handlerRef.current = handler;
  useEffect(() => {
    const internalHandler = async (args: EventMap[K]) => {
      return handlerRef.current(args);
    };
    const unsubscribe = eventEmitter.subscribe(event, internalHandler);
    return () => unsubscribe();
  }, [event]);
}

export function useEventEmitter<K extends keyof EventMap>() {
  return {
    subscribe: useCallback(
      <K extends keyof EventMap>(
        event: K,
        handler: (args: EventMap[K]) => Promise<void>
      ) => eventEmitter.subscribe(event, handler),
      []
    ),
    triggerEvent: useCallback(
      async <T extends K>(event: T, args: EventMap[T]) => {
        await eventEmitter.trigger(event, args);
      },
      []
    ),
  };
}
